/**
 * Feeds SCHEDULER-started executions into the Agents sidebar
 * (`recjobs-lexide-pane-handoff-2026-07-18.md` §3.5(b)).
 *
 * Why this hook exists at all. Scheduler work reaches the sidebar by two
 * different roads:
 *   - `task` / `watch` jobs dispatch the `lexiun-job-runner` harness, so
 *     they become ORDINARY harness tasks and `useHarnTasksPoll` already
 *     sees them — nothing to do here (and nothing to build; they light up
 *     when Part F1 lands).
 *   - `reminder` / `system` jobs are PURE cron agent turns. They never
 *     touch harn-service, so no task record exists and the sidebar has
 *     nothing to poll. Their only trace is the 30-day execution log.
 * This hook covers the second road by reading `schedule.runs`.
 *
 * Two alternatives were considered and REJECTED upstream (§3.5), so they
 * are not re-attempted here: routing reminders through a harness purely
 * to make them visible, and synthesizing fake harn-task records.
 *
 * Freshness: `schedule.changed {kind:"ran"}` fires on every completion, so
 * the merge is live rather than poll-only. Per §2.4 the broadcast is an
 * OPTIMISATION, never the source of truth — WS delivery can miss, and the
 * captured global broadcast is only populated after the SPA has issued at
 * least one gateway request (RT-1). A slow interval poll therefore stays
 * as the floor.
 *
 * Fail-soft: a runtime without the `schedule.*` methods (or with the
 * subsystem disabled by org policy) simply yields an empty list. Scheduler
 * rows are an ENRICHMENT of the pane — their absence must never blank the
 * missions and harness rows that share it.
 *
 * CASE BINDING — why this hook also calls `schedule.list`. The run-log row
 * (`recurring-jobs/run-log.ts`) records jobId/kind/title/status/timings and
 * NOT the job's scope, so `schedule.runs` alone cannot say which case a run
 * belonged to. The JOB view does carry it (`toJobView` spreads
 * `job.scope.caseId`), so the binding is recovered by joining runs → jobs on
 * `jobId`. This matters for correctness, not just decoration: without the
 * join every scheduled row looks unbound, and a run belonging to a case
 * would be badged "sem caso" — a false statement about the lawyer's data.
 * A job that no longer appears in `schedule.list` (deleted, or the call
 * failed) is left UNKNOWN rather than assumed unbound.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { useLexEventStream } from "@/hooks/useEventStream";
import { useGatewayStore } from "@/stores/useGatewayStore";

/** One row of `schedule.runs` — handoff §2.5 `RunLogRow`. */
export interface ScheduledRunRow {
  /** ISO timestamp of the run. */
  ts: string;
  jobId: string;
  kind: string;
  title: string;
  status: "ok" | "error" | "skipped";
  summary?: string;
  durationMs?: number;
  delivered?: boolean;
  deliveryStatus?: string;
  model?: string;
  usage?: {
    input_tokens?: number;
    output_tokens?: number;
    total_tokens?: number;
  };
  sessionKey?: string;
  error?: string;
  /** Case binding, resolved by joining the owning job (NOT present on the
   *  wire — see the header). `undefined` means UNKNOWN, not unbound —
   *  a row with no case simply shows none either way. */
  caseId?: string;
}

/** Slow floor beneath the `schedule.changed` broadcast. Deliberately long:
 *  the broadcast carries freshness, this only repairs a missed one. */
export const SCHEDULE_POLL_MS = 60_000;

/** Rows fetched per poll. The sidebar shows recent activity, not history —
 *  the Schedule pane owns the full 30-day log. */
export const SCHEDULE_RUNS_LIMIT = 20;

export interface UseScheduledRunsResult {
  runs: ScheduledRunRow[];
  refetch: () => Promise<void>;
}

/**
 * @param caseId Active case, or null for the cross-case view.
 *
 * SCOPING DECISION (the product call §3.5 left to LexIDE, recorded here
 * and in the handback): a run bound to ANOTHER case is filtered out, but
 * an UNBOUND run is shown in EVERY view and badged "sem caso" by the row.
 *
 * Rationale: the whole point of this section is that scheduler work must
 * not be invisible. Hiding unbound runs whenever a case happens to be open
 * would recreate exactly that failure — the lawyer's nightly job would
 * silently vanish the moment they opened a matter, which is precisely when
 * they are most likely to be looking at this pane. Showing it with an
 * honest "no case" badge keeps the run visible without ever implying it
 * belongs to the case currently on screen.
 */
export function useScheduledRunsPoll(
  caseId: string | null,
): UseScheduledRunsResult {
  const rpc = useGatewayStore((s) => s.rpc);
  const [runs, setRuns] = useState<ScheduledRunRow[]>([]);
  const disposedRef = useRef(false);

  // `rpc` IS a dependency, deliberately not a ref. The gateway client is
  // null on first paint and only appears once the socket connects, so a
  // ref-based fetch fires once against null, silently no-ops, and leaves
  // the tab empty until the next 60s tick. Keying on `rpc` re-fires the
  // moment the client is ready.
  const fetchRuns = useCallback(async () => {
    if (!rpc) return;
    try {
      // Jobs are fetched alongside the runs purely to recover the case
      // binding the run log omits. A failure here degrades the rows to
      // "case unknown"; it must not lose the runs themselves.
      const [res, jobsRes] = await Promise.all([
        rpc.call<{ runs?: ScheduledRunRow[] }>("schedule.runs", {
          limit: SCHEDULE_RUNS_LIMIT,
        }),
        rpc
          .call<{ jobs?: Array<{ id?: string; caseId?: string }> }>(
            "schedule.list",
            {},
          )
          .catch(() => ({ jobs: [] as Array<{ id?: string; caseId?: string }> })),
      ]);
      if (disposedRef.current) return;
      const jobCaseById = new Map<string, string | undefined>();
      for (const job of jobsRes?.jobs ?? []) {
        if (job?.id) jobCaseById.set(job.id, job.caseId);
      }
      const rows = Array.isArray(res?.runs) ? res.runs : [];
      setRuns(
        rows
          .filter((r) => r && typeof r.ts === "string")
          .map((r) => ({ ...r, caseId: jobCaseById.get(r.jobId) }))
          .filter((r) => {
            // Unbound (or not-yet-resolved) runs surface everywhere — see
            // the scoping decision above.
            if (!r.caseId) return true;
            return caseId === null || r.caseId === caseId;
          }),
      );
    } catch {
      // Enrichment only — and deliberately KEEP whatever we last showed.
      // Blanking the list on a failed fetch would assert "no scheduled
      // work ran", which is a claim we cannot make from a request that
      // never got an answer. Rows only ever appear after a SUCCESSFUL
      // fetch, so retaining them cannot invent work that never existed;
      // a genuinely disabled subsystem simply never populates them.
    }
  }, [caseId, rpc]);

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);

  // Live: every completed scheduled job announces itself.
  useLexEventStream(
    "schedule.changed",
    useCallback(
      (payload: unknown) => {
        const kind = (payload as { kind?: string } | null)?.kind;
        // Only a completed RUN adds a sidebar row; create/pause/delete are
        // Schedule-pane concerns.
        if (kind && kind !== "ran") return;
        void fetchRuns();
      },
      [fetchRuns],
    ),
  );

  // Poll floor — repairs a missed broadcast (§2.4 gotchas).
  useEffect(() => {
    void fetchRuns();
    const timer = setInterval(() => void fetchRuns(), SCHEDULE_POLL_MS);
    return () => clearInterval(timer);
  }, [fetchRuns]);

  return { runs, refetch: fetchRuns };
}
